import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import Circle from './Circle';
import Marker from './Marker';
import Polygon from './Polygon';

const INIITAL_ZOOM = 10;
const MAX_ZOOM = 15;
const INITIAL_CENTER = { lat: 59.329323, lng: 18.068581 };
const INITIAL_MAP_OPTIONS = {
  zoom: INIITAL_ZOOM,
  maxZoom: MAX_ZOOM,
  center: INITIAL_CENTER,
};

export default class GoogleMaps extends Component {
  static loaded = false;
  static tag = null;

  /**
   * Returns a promise that resolves to the google object.
   * @returns {Promise}
   */
  static ensureScriptLoaded() {
    return new Promise((resolve) => {
      if (GoogleMaps.loaded) {
        resolve(window.google);
      }

      GoogleMaps.scriptTag().addEventListener('load', () => {
        GoogleMaps.loaded = true;
        return resolve(window.google);
      });
    });
  }

  static scriptTag() {
    if (GoogleMaps.tag !== null) {
      return GoogleMaps.tag;
    }

    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.async = false;
    scriptTag.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyBLwJiPTuWRV1X2ggEQtNgNnnx_pWhD-6E';
    const body = document.getElementsByTagName('body')[0];
    body.appendChild(scriptTag);
    GoogleMaps.tag = scriptTag;
    return GoogleMaps.tag;
  }


  static defaultProps = {
    width: 483,
    height: 300,
    markers: [],
  };

  static propTypes = {
    markers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        coordinates: PropTypes.oneOfType([
          PropTypes.arrayOf(Circle.propTypes.coordinates),
          Polygon.propTypes.paths,
        ]).isRequired,
      }),
    ),
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  };

  constructor(props) {
    super(props);
    this.ref = null;
    this.state = {
      loaded: false,
      google: null,
      map: null,
      boundsById: {}, // Object of bounds-arrays
      viewBounds: null,
    };
  }

  componentWillMount() {
    GoogleMaps.ensureScriptLoaded().then(google =>
      this.setState({
        loaded: true,
        google,
      }));
  }

  componentWillUpdate(nextProps, nextState) {
    if (!this.state.loaded && nextState.loaded) {
      this.loadMap(nextState.google.maps);
    }

    if (!isEqual(this.state.boundsById, nextState.boundsById)) {
      const filteredBounds = Object.values(nextState.boundsById).filter(subBound => subBound);

      if (filteredBounds.length === 0) {
        this.setState({ viewBounds: null });
      } else {
        this.setState({
          viewBounds: filteredBounds
            .reduce((bounds, subBound) => bounds.union(subBound),
              new this.state.google.maps.LatLngBounds(),
            ),
        });
      }
    }

    if (!isEqual(this.state.viewBounds, nextState.viewBounds)) {
      if (!nextState.viewBounds) {
        nextState.map.setOptions(INITIAL_MAP_OPTIONS);
      } else {
        this.state.map.fitBounds(nextState.viewBounds);
      }
    }
  }

  loadMap(maps) {
    const map = new maps.Map(this.ref, INITIAL_MAP_OPTIONS);
    this.setState({ map });
  }

  handleBounds(bounds, id) {
    this.setState(prevState => ({
      boundsById: {
        ...prevState.boundsById,
        [id]: bounds,
      },
    }));
  }

  render() {
    const style = {
      width: `${this.props.width}px`,
      height: `${this.props.height}px`,
    };

    return (
      <div style={style} ref={(ref) => { this.ref = ref; }}>
        {this.state.loaded
        && this.state.map
        && this.props.markers
          .filter(geoCode => geoCode)
          .map((geoCode) => {
            const sharedProps = {
              maps: this.state.google.maps,
              map: this.state.map,
              handleBounds: bounds => this.handleBounds(bounds, geoCode.id),
            };

            if (geoCode.type === 'point') {
              return (
                <Circle
                  key={geoCode.id}
                  {...sharedProps}
                  coordinates={geoCode.coordinates[0]}
                  radius={geoCode.distance * 1000}
                  info={{
                    title: geoCode.name,
                    content: geoCode.name,
                  }}
                  fillColor={geoCode.fillColor}
                  strokeColor={geoCode.strokeColor}
                />
              );
            }

            if (geoCode.type === 'polygon' || geoCode.type === 'multipolygon') {
              return (
                <Polygon
                  key={geoCode.id}
                  {...sharedProps}
                  paths={geoCode.coordinates}
                  info={{
                    title: geoCode.name,
                    content: geoCode.name,
                  }}
                />
              );
            }

            if (geoCode.type === 'marker') {
              return (
                <Marker
                  key={geoCode.id}
                  {...sharedProps}
                  coordinates={geoCode.coordinates[0]}
                  icon={geoCode.icon}
                  info={{
                    title: geoCode.name,
                    content: geoCode.name,
                  }}
                />
              );
            }

            return null;
          })}
      </div>
    );
  }
}
